import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AutoAssignSettingsUpdatePayload,
  CreateWorkspacePayload,
  TrackingPageSettingsUpdatePayload,
  UpdatePublicFormSettingsPayload,
  UpdateWorkspacePayload,
} from "@web-app/types";

import {
  create,
  getCurrentWorkspace,
  update,
  updateAutoAssignSettings,
  updateWorkspacePublicFormSettings,
  updateWorkspaceTrackingPageSettings,
} from "../services/workspaces";

const queryKey = "workspaces";
const currentWorkspaceQueryKey = "currentWorkspace";

export const useCurrentWorkspace = () => {
  return useQuery({
    queryFn: getCurrentWorkspace,
    queryKey: [currentWorkspaceQueryKey],
  });
};

export const useCreateWorkspaceMutation = () => {
  const queryClient = useQueryClient();

  const createWorkspace = useMutation({
    mutationFn: async (payload: CreateWorkspacePayload) => {
      const response = await create(payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
    },
  });

  return {
    createWorkspace,
  };
};

export const useWorkspaceMutation = () => {
  const queryClient = useQueryClient();

  const updateWorkspace = useMutation({
    mutationFn: async (params: {
      id: string;
      payload: Partial<UpdateWorkspacePayload>;
    }) => {
      const response = await update(params.id, params.payload);
      return response;
    },
    onSuccess: () => {
      // @ts-ignore - check this
      queryClient.invalidateQueries([queryKey]);
    },
  });

  return {
    updateWorkspace,
  };
};

export const useWorkspacePublicFormSettingsMutation = () => {
  const queryClient = useQueryClient();

  const updatePublicFormSettings = useMutation({
    mutationFn: async (payload: Partial<UpdatePublicFormSettingsPayload>) => {
      const response = await updateWorkspacePublicFormSettings(payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      queryClient.invalidateQueries({ queryKey: [currentWorkspaceQueryKey] });
    },
  });

  return {
    updatePublicFormSettings,
  };
};

export const useWorkspaceTrackingPageSettingsMutation = () => {
  const queryClient = useQueryClient();

  const updateTrackingPageSettings = useMutation({
    mutationFn: async (payload: Partial<TrackingPageSettingsUpdatePayload>) => {
      const response = await updateWorkspaceTrackingPageSettings(payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      queryClient.invalidateQueries({ queryKey: [currentWorkspaceQueryKey] });
    },
  });

  return {
    updateTrackingPageSettings,
  };
};

export const useAutoAssignSettingsMutation = () => {
  const queryClient = useQueryClient();

  const mutateAutoAssignSettings = useMutation({
    mutationFn: async (payload: AutoAssignSettingsUpdatePayload) => {
      const response = await updateAutoAssignSettings(payload);
      return response;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [queryKey] });
      queryClient.invalidateQueries({ queryKey: [currentWorkspaceQueryKey] });
    },
  });

  return {
    mutateAutoAssignSettings,
  };
};
